/**
 * Keyed English error catalog (DEV-290).
 *
 * Stable keys now — swap to i18n later without touching call sites.
 * NO runtime deps (no React / RN) — safe for web + mobile via `@6tool/shared/errors`
 * or `@shared/errors`.
 *
 * @module shared/errors/messages
 */

/** Canonical English copy. `{var}` tokens are filled by {@link formatErrorMessage}. */
export const ERROR_MESSAGES = {
  default: 'Something went wrong. Please try again.',
  network: 'No internet connection. Please check your network and try again.',
  signInFailed: 'Sign in failed. Please try again.',
  authentication: 'An authentication error occurred. Please try again.',

  // Firebase Auth
  'auth.emailAlreadyInUse':
    'An account with this email already exists. Please log in or reset password.',
  'auth.weakPassword': 'Password is too weak. Please choose a stronger password.',
  'auth.invalidEmail': 'Please enter a valid email address.',
  'auth.invalidPhoneNumber': 'Invalid phone number. Please check and try again.',
  'auth.invalidVerificationCode': 'Invalid verification code. Please try again.',
  'auth.codeExpired': 'Verification code expired. Please request a new one.',
  'auth.networkRequestFailed': 'No internet connection. Please check your network and try again.',
  'auth.tooManyRequests': 'Too many requests. Please try again later.',
  'auth.quotaExceeded': 'Service quota exceeded. Please try again later.',
  'auth.internalError': 'An authentication error occurred. Please try again.',
  'auth.wrongPassword': 'Incorrect email or password. Please try again.',
  'auth.userNotFound': 'Incorrect email or password. Please try again.',
  'auth.userDisabled': 'This account has been disabled. Contact support@6-tool.com.',
  'auth.requiresRecentLogin': 'Please sign in again to continue.',
  'auth.invalidCredential': 'That sign-in could not be verified. Please try again.',
  'auth.credentialAlreadyInUse': 'This sign-in method is already linked to another account.',
  'auth.accountExistsDifferentCredential':
    'An account already exists with this email. Sign in with your original method to link them.',
  'auth.operationNotAllowed':
    'This sign-in method is not enabled. Please try another method or contact support.',
  'auth.popupClosed': 'Sign-in was cancelled.',

  // Callable / Functions (with and without functions/ prefix — normalized at lookup)
  'functions.unavailable': 'No internet connection. Please check your network and try again.',
  'functions.alreadyExists':
    'An account with this email already exists. Please log in or reset password.',
  'functions.notFound': 'The requested item could not be found.',
  'functions.permissionDenied': 'You do not have permission to perform this action.',
  'functions.unauthenticated': 'Please sign in to continue.',
  'functions.deadlineExceeded': 'Request timed out. Please try again.',
  'functions.resourceExhausted': 'Service is temporarily unavailable. Please try again later.',
  'functions.invalidArgument': 'Please check the form and try again.',
  'functions.failedPrecondition': 'We could not complete this action. Please review and retry.',
  'functions.internal': 'An error occurred. Please try again.',

  // Domain-specific allowlist fallbacks
  'username.taken': 'This username is already taken. Please choose a different username.',
  'username.invalid': 'Invalid username. Please check and try again.',
  'login.usernameNotFound': 'No account found for that username. Please check and try again.',
  'child.createFailed': 'Failed to create child account. Please try again.',
  'child.notFound': 'Child account not found or is not linked to your account.',
  'child.consentRequired':
    'Parental consent is missing or revoked for this child. Please contact support to restore access.',
  'signup.sessionExpired':
    'This signup session has expired. Please start the verification process again from the beginning (verify phone, then email).',
  'emulator.dataConnect':
    'Data Connect emulator is not running. Please start the emulators: ./scripts/start_staging_emulators.sh',
} as const;

export type ErrorMessageKey = keyof typeof ERROR_MESSAGES;

/** Action-scoped defaults when no code maps (call sites pass `action`). */
export const ACTION_DEFAULTS = {
  saveProfile: 'Failed to update profile. Please try again.',
  updateEmail: 'Failed to update email. Please try again.',
  checkUsername: 'Could not check username availability. Please try again.',
  setUsername: 'Could not set username. Please try again.',
  acceptInvite: 'Failed to accept invite. Please try again.',
  declineInvite: 'Failed to decline invite. Please try again.',
  approveInvite: 'Failed to approve request. Please try again.',
  joinTeam: 'Failed to join team. Please try again.',
  leaveTeam: 'Failed to leave team. Please try again.',
  saveTeam: 'Failed to save team. Please try again.',
  deleteTeam: 'Failed to delete team. Please try again.',
  addPlayer: 'Failed to add player. Please try again.',
  updatePlayer: 'Failed to update player. Please try again.',
  removePlayer: 'Failed to remove player. Please try again.',
  createChild: 'Failed to create child account. Please try again.',
  updateChild: 'Failed to update child. Please try again.',
  deleteChild: 'Failed to delete child. Please try again.',
  switchChild: 'Failed to switch to child account. Please try again.',
  updatePassword: 'Failed to update password. Please try again.',
  linkProvider: 'Failed to link provider. Please try again.',
  unlinkProvider: 'Failed to unlink provider. Please try again.',
  markNotificationsRead: 'Failed to mark all as read. Please try again.',
  signIn: 'Sign in failed. Please try again.',
  signUp: 'Failed to create account. Please try again.',
  accountRecovery: 'Could not complete account recovery. Please try again.',
  loadInvites: 'Failed to load invites. Please try again.',
} as const;

export type ActionDefaultKey = keyof typeof ACTION_DEFAULTS;

/**
 * Map Firebase / callable error codes (normalized lowercase, optional functions/ strip)
 * to catalog keys.
 */
export const CODE_TO_MESSAGE_KEY: Record<string, ErrorMessageKey> = {
  'auth/email-already-in-use': 'auth.emailAlreadyInUse',
  'auth/email-already-exists': 'auth.emailAlreadyInUse',
  'auth/weak-password': 'auth.weakPassword',
  'auth/invalid-email': 'auth.invalidEmail',
  'auth/invalid-phone-number': 'auth.invalidPhoneNumber',
  'auth/invalid-verification-code': 'auth.invalidVerificationCode',
  'auth/invalid-verification-id': 'auth.invalidVerificationCode',
  'auth/code-expired': 'auth.codeExpired',
  'auth/network-request-failed': 'auth.networkRequestFailed',
  'auth/too-many-requests': 'auth.tooManyRequests',
  'auth/quota-exceeded': 'auth.quotaExceeded',
  'auth/internal-error': 'auth.internalError',
  'auth/wrong-password': 'auth.wrongPassword',
  'auth/user-not-found': 'auth.userNotFound',
  'auth/user-disabled': 'auth.userDisabled',
  'auth/requires-recent-login': 'auth.requiresRecentLogin',
  'auth/invalid-credential': 'auth.invalidCredential',
  'auth/credential-already-in-use': 'auth.credentialAlreadyInUse',
  'auth/account-exists-with-different-credential': 'auth.accountExistsDifferentCredential',
  'auth/operation-not-allowed': 'auth.operationNotAllowed',
  'auth/popup-closed-by-user': 'auth.popupClosed',
  'auth/cancelled-popup-request': 'auth.popupClosed',

  'functions/unavailable': 'functions.unavailable',
  unavailable: 'functions.unavailable',
  'functions/already-exists': 'functions.alreadyExists',
  'already-exists': 'functions.alreadyExists',
  'functions/not-found': 'functions.notFound',
  'not-found': 'functions.notFound',
  'functions/permission-denied': 'functions.permissionDenied',
  'permission-denied': 'functions.permissionDenied',
  'functions/unauthenticated': 'functions.unauthenticated',
  unauthenticated: 'functions.unauthenticated',
  'functions/deadline-exceeded': 'functions.deadlineExceeded',
  'deadline-exceeded': 'functions.deadlineExceeded',
  'functions/resource-exhausted': 'functions.resourceExhausted',
  'resource-exhausted': 'functions.resourceExhausted',
  'functions/invalid-argument': 'functions.invalidArgument',
  'invalid-argument': 'functions.invalidArgument',
  'functions/failed-precondition': 'functions.failedPrecondition',
  'failed-precondition': 'functions.failedPrecondition',
  'functions/internal': 'functions.internal',
  internal: 'functions.internal',

  // Pre-Auth login resolution (DEV-604) — not a Firebase / functions code
  'login/username-not-found': 'login.usernameNotFound',
};

/**
 * Fill `{token}` placeholders in a template. Missing vars are left as-is.
 */
export function formatErrorMessage(
  template: string,
  vars: Record<string, string | number> = {}
): string {
  if (typeof template !== 'string') return '';
  return template.replace(/\{(\w+)\}/g, (match, key) =>
    Object.prototype.hasOwnProperty.call(vars, key) ? String(vars[key]) : match
  );
}

export const DEFAULT_ERROR_MESSAGE = ERROR_MESSAGES.default;
export const MAX_SERVER_MESSAGE_LENGTH = 160;
